import { lang } from 'enablex/shared'
import { AuthStrategyConfig } from 'common/typings'
import { get } from 'lodash'
import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'

import api from '~/app/api'
import { ExtendedHistory } from '~/app/history'
import BasicAuthentication, { setActiveWorkspace, setChatUserAuth } from '~/auth/basicAuth'

import { AuthMethodPicker } from './AuthMethodPicker'
import LoginContainer from './LoginContainer'
import { LoginForm } from './LoginForm'

type RouterProps = RouteComponentProps<
  { strategy: string; workspace: string },
  {},
  { registerUrl?: string; from?: string; email?: string; password?: string; loginUrl?: string }
>

type Props = { auth: BasicAuthentication } & RouterProps & ExtendedHistory

interface AuthConfigResponse {
  payload: {
    strategies: AuthStrategyConfig[]
    isFirstUser: boolean
  }
}

const Login: FC<Props> = props => {
  const [isLoading, setLoading] = useState(true)
  const [isFirstUser, setFirstUser] = useState(false)
  const [strategies, setStrategies] = useState<AuthStrategyConfig[]>()
  const [loginUrl, setLoginUrl] = useState('')
  const [redirectTo, setRedirectTo] = useState<string>()
  const [error, setError] = useState<string | null>()
  const [domainLogo, setDomainLogo] = useState('')
  const [domainWorkspace, setDomainWorkspace] = useState('')
  const [pageNotFound, setPageNotFound] = useState(false)

  useEffect(() => {
    onStrategyChanged()
  }, [props.match.params.strategy, isLoading])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initialize()
  }, [])

  const initialize = async () => {
    const routeWorkspaceId = props.match.params.workspace
    const { workspaceId, botId, sessionId, signature, error } = props.location.query

    if (routeWorkspaceId || workspaceId) {
      await setActiveWorkspace(routeWorkspaceId || workspaceId)
    }

    if (botId && sessionId && signature) {
      setChatUserAuth({ botId, sessionId, signature })
    }

    if (error) {
      setError(error)
    }

    if (props.auth.isAuthenticated()) {
      await props.auth.afterLoginRedirect()
    }

    if (!strategies) {
      await loadAuthConfig()
    }
  }

  const onStrategyChanged = () => {
    selectStrategy(props.match.params.strategy)

    if (strategies && strategies.length === 1) {
      updateUrlStrategy(strategies[0].strategyId)
      selectStrategy(strategies[0].strategyId)
    }

    if (props.location.state) {
      setRedirectTo(props.location.state.from)
    }
  }

  const setPageBranding = (branding) => {
    window.document.title = branding.pageTitle || 'EnableX Dialogs'
    window.APP_NAME = branding.pageTitle || 'EnableX Dialogs'

    if (branding.faviconURL) {
      const faviconPath = `/api/v1/bots/___/media/${branding.workspaceId}/${branding.faviconURL}`
      const link = document.querySelector('link[rel="icon"]')
      link && link.setAttribute('href', faviconPath)
      window.APP_FAVICON = faviconPath
    }
  }

  const loadAuthConfig = async () => {
    const { data } = await api.getAnonymous().get<AuthConfigResponse>('/admin/auth/config')
    const response = await api.getAnonymous().get<any>('/admin/auth/branding')
    // console.log('branding: ', JSON.stringify(response.data))
    const {payload} = response.data
    if(payload.result === true) {
      setDomainLogo(payload.branding?.logoURL || '')
      setPageBranding({...payload.branding, workspaceId: payload.workspaceId})
      setDomainWorkspace(payload.workspaceId)
      setStrategies(data.payload.strategies.filter(x => !x.hidden))
      setFirstUser(data.payload.isFirstUser)
      setLoading(false)
    } else {
      setDomainLogo('')
      setDomainWorkspace('')
      setPageNotFound(true)
      setLoading(false)
    }
  }

  const updateUrlStrategy = (strategyId: string) => props.history.push({ pathname: `/login/${strategyId}` })

  const selectStrategy = (id: string) => {
    const strategy = strategies && strategies.find(x => x.strategyId === id)
    if (!strategy) {
      return setLoginUrl('')
    }

    setError(undefined)

    const { strategyType, strategyId, registerUrl } = strategy

    if (strategyType === 'saml' || strategyType === 'oauth2') {
      return (window.location.href = `${api.getApiPath()}/admin/auth/redirect/${strategyType}/${strategyId}`)
    }

    if (isFirstUser) {
      props.history.push({ pathname: '/register', state: { registerUrl } })
    } else {
      setLoginUrl(strategy.loginUrl!)
    }
  }

  const loginUser = async (email: string, password: string) => {
    try {
      setError(undefined)
      await props.auth.login({ email, password }, loginUrl, redirectTo)
    } catch (err) {
      if (err.type === 'PasswordExpiredError') {
        props.history.push({ pathname: '/changePassword', state: { email, password, loginUrl } })
      } else {
        setError(get(err, 'response.data.message', err.message))
      }
    }
  }

  if(pageNotFound) {
    return (
      <div style={{display: 'flex',  justifyContent: 'center', alignItems: 'center', height: '100vh',  margin: '0'}}>
        <div style={{textAlign: 'center',fontSize: '1em'}}>
          <h1>404 Page Not Found</h1>
          <p>Sorry, the page you are looking for does not exist.</p>
        </div>
      </div>
    )
  }

  if (isLoading || !strategies) {
    return null
  }

  return (
      <LoginContainer title={lang.tr('admin.login')} error={error} poweredBy={true} domainLogo={domainLogo} domainWorkspace={domainWorkspace}>
      {loginUrl ? (
        <LoginForm onLogin={loginUser} />
      ) : (
        <AuthMethodPicker strategies={strategies} onStrategySelected={updateUrlStrategy} />
      )}
    </LoginContainer>
  )
}

export default Login
